<template>
    <div
        class="flex lg:grid flex-col lg:grid-cols-[repeat(24,_minmax(0,_1fr))] w-full"
        :class="spaceBetweenClasses"
        :style="spaceBetweenStyle"
    >
        <BlocksAnimation
            v-for="(item, index) in layoutBlocks"
            :key="item.canonicalUid"
            :animation-offset="index"
            :style="{
                'grid-column': `${item.grid.x + 1} / ${item.grid.x + item.grid.w + 1}`,
                'grid-row': `${item.grid.y + 1} / ${item.grid.y + item.grid.h + 1}`,
            }"
        > 
            <BlockWrapper
                :block="item"
                :class="`type-${item.__typename}-${item.typeIndex}`"
                is-layout
                :is-singleton="isSingleton(item)"
            />
        </BlocksAnimation>
    </div>
</template>

<script setup>
    import BlockWrapper from '~/components/blocks/BlockWrapper.vue'

    const props = defineProps({
        block: Object
    })

    const { queryContentBuilder } = useContentBuilder()

    const contentQuery = await queryContentBuilder(props.block)

    const { data: contentData, error: contentError } = await useAsyncQuery({
        query: contentQuery,
        variables: {
            canonicalUid: props.block?.canonicalUid
        },
        key: `${props.block?.canonicalUid}-layout`,
        cache: false,
    })

    if (!isEmpty(contentError.value)) {
        throw createError(contentError.value)
    }

    const spaceBetweenClasses = computed(() => {
        switch (props.block?.SpaceBetween) {
            case 'none':
                return ''
            case 'sm':
                return 'gap-4'
            case 'md':
            default:
                return 'gap-6'
            case 'lg':
                return 'gap-8'
            case 'xl':
                return 'gap-10'
        }
    })

    const spaceBetweenStyle = computed(() => {
        if (props.block?.SpaceBetween !== 'none') {
            return
        }

        return '--radius-card-base: 0px;'
    })
    const typeCounts = {}
    const layoutBlocks = computed(() => {
        // map with grid data
        const gridLayout = JSON.parse(get(props.block, 'contentBuilderLayout.grid') ?? '[]')

        return map(get(contentData.value?.entry, 'layout_contentBuilder'), (contentBlock, index) => {
            // index is a string in the response
            const layout = find(gridLayout, {index: `${index}`})

            // typeCounts is used to keep track of the index of each type
            if (!typeCounts[contentBlock.__typename]) {
                typeCounts[contentBlock.__typename] = 0
            }
            // for news & events we don't want to increment the index if there is an article selected
            if (['ds_EventsLayoutBlock_Entry', 'ds_NewsLayoutBlock_Entry'].includes(contentBlock.__typename)) {
                const hasSelectedArticle = contentBlock?.news_SelectArticle ?? contentBlock?.events_SelectArticle ?? []

                if(!hasSelectedArticle.length) {
                    typeCounts[contentBlock.__typename]++
                }
            } else {
                typeCounts[contentBlock.__typename]++
            }

            return {
                ...contentBlock,
                typeIndex: typeCounts[contentBlock.__typename],
                grid: {
                    x: layout.x,
                    y: layout.y,
                    w: layout.width,
                    h: layout.height,
                }
            }

        })
    })
    // checks if this block is by itself on its own row
    const isSingleton = (block) => {
        return block.grid.x === 0 && block.grid.w === 24
    }
</script>